import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, GeoJSON, Marker, Circle, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { getParcels, getStats, getCommunes, getCultures, authMe, runDetection, getSubscriptionStatus, authFetch } from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import { cadastreToPoints, cadLabel } from "@/components/MapSearch";
import ParcelDetail from "@/components/ParcelDetail";
import ManualParcelEditor from "@/components/ManualParcelEditor";
import AdminUsers from "@/components/AdminUsers";
import AdminAttachPanel from "@/components/AdminAttachPanel";
import CarnetParcelle from "@/components/CarnetParcelle";
import ProDashboard from "@/components/ProDashboard";
import WeatherWidget from "@/components/WeatherWidget";
import RegionToggle, { REGIONS as REGION_DEFS, getStoredRegion, setStoredRegion } from "@/components/RegionToggle";
import { LogOut, Menu, X, RefreshCw, Layers, Radar, Loader2, PenLine, Users, Navigation, Leaf, LayoutDashboard, Droplets, Wrench, ChevronDown, Calculator, Star, User, Shield, Lock, Link2 , FileSignature } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { authLogout } from "@/lib/api";
import { usePendingLockRequests, PendingBadge } from "@/hooks/usePendingLockRequests";
import { toast } from "sonner";
import { TILE_LAYERS, MAX_ZOOM } from "@/lib/mapLayers";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REUNION_CENTER = [-21.1151, 55.5364];
const REUNION_ZOOM = 11;
const MAYOTTE_CENTER = [-12.8275, 45.166];
const MAYOTTE_ZOOM = 11;

// mobileSheet : sur téléphone, la fiche couvre jusqu'à 70 % du bas de la carte → la parcelle est
// cadrée dans la partie haute restée visible (sous les boutons), sinon elle était cachée par la fiche.
function FlyToParcel({ parcel, mobileSheet = false }) {
  const map = useMap();
  useEffect(() => {
    if (!parcel?.geometry?.coordinates) return;
    const geom = parcel.geometry;
    let coords = [];
    if (geom.type === "Polygon" && geom.coordinates[0]) coords = geom.coordinates[0];
    else if (geom.type === "MultiPolygon" && geom.coordinates[0]?.[0]) coords = geom.coordinates[0][0];
    if (coords.length > 0) {
      const lats = coords.map(c => c[1]);
      const lngs = coords.map(c => c[0]);
      const bounds = [[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]];
      const opts = mobileSheet
        ? { paddingTopLeft: [24, 72], paddingBottomRight: [24, Math.round(map.getSize().y * 0.7) + 16], maxZoom: 16, duration: 0.8 }
        : { padding: [80, 80], maxZoom: 16, duration: 0.8 };
      map.flyToBounds(bounds, opts);
    }
  }, [parcel, map, mobileSheet]);
  return null;
}

function MapPositionTracker({ onMove }) {
  useMapEvents({
    moveend: (e) => {
      const map = e.target;
      const c = map.getCenter();
      onMove({ lat: c.lat, lng: c.lng, zoom: map.getZoom() });
    },
  });
  return null;
}

// Ferme les bulles pendant le déplacement de la carte (évite les tooltips fantômes au drag).
function TooltipDragCleaner() {
  const map = useMap();
  useEffect(() => {
    const closeAll = () => map.eachLayer((l) => { if (l.closeTooltip) l.closeTooltip(); });
    map.on('movestart', closeAll);
    map.on('moveend', closeAll);
    return () => { map.off('movestart', closeAll); map.off('moveend', closeAll); };
  }, [map]);
  return null;
}

// User GPS location — pulsating blue dot (Google Maps style)
const USER_LOCATION_ICON = L.divIcon({
  className: "user-location-marker",
  html: '<div class="ulm-pulse"></div><div class="ulm-dot"></div>',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

function UserLocationLayer({ location }) {
  if (!location) return null;
  return (
    <>
      <Circle
        center={[location.lat, location.lng]}
        radius={Math.max(location.accuracy || 30, 10)}
        pathOptions={{ color: '#2563eb', fillColor: '#2563eb', fillOpacity: 0.08, weight: 1, opacity: 0.45 }}
        interactive={false}
      />
      <Marker
        position={[location.lat, location.lng]}
        icon={USER_LOCATION_ICON}
        interactive={false}
        keyboard={false}
        zIndexOffset={1000}
      />
    </>
  );
}

// Hook for responsive detection
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return isMobile;
}

export default function MapDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [user, setUser] = useState(location.state?.user || null);
  const [parcels, setParcels] = useState([]);
  const [stats, setStats] = useState(null);
  const [communes, setCommunes] = useState([]);
  const [cultures, setCultures] = useState([]);
  const [selectedParcel, setSelectedParcel] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [filterCommune, setFilterCommune] = useState("");
  const [filterCulture, setFilterCulture] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeLayer, setActiveLayer] = useState("esri");
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [detecting, setDetecting] = useState(false);
  const [detectionResult, setDetectionResult] = useState(null);
  const [viewMode, setViewMode] = useState("map");
  // Recherche cadastrale : contours affichés sur la carte, et pré-remplissage du tracé manuel
  const [cadastreShapes, setCadastreShapes] = useState([]);
  const [manualPrefill, setManualPrefill] = useState(null);
  const [mapPosition, setMapPosition] = useState(() => {
    const r = getStoredRegion();
    const c = r === "mayotte" ? MAYOTTE_CENTER : REUNION_CENTER;
    const z = r === "mayotte" ? MAYOTTE_ZOOM : REUNION_ZOOM;
    return { lat: c[0], lng: c[1], zoom: z };
  });
  const [locating, setLocating] = useState(false);
  const [userLocation, setUserLocation] = useState(null); // { lat, lng, accuracy }
  const watchIdRef = useRef(null);
  const [mobileTab, setMobileTab] = useState("carte"); // "carte" | "outils"
  const mapRef = useRef(null);
  const openTooltipsRef = useRef(new Set());  // bulles ouvertes → on n'en garde qu'une à la fois
  const [isAdmin, setIsAdmin] = useState(false);
  const pendingLocks = usePendingLockRequests(isAdmin);
  const [isPro, setIsPro] = useState(false);
  const [phytoParcel, setPhytoParcel] = useState(null);
  // Carnet : même page que depuis le tableau de bord (barre Météo · Carte · Vigie · Recalibrer) ;
  // le retour ramène sur la carte, centrée sur la parcelle
  const openCarnet = (p) => { if (p?.parcel_id) navigate(`/carnet/${encodeURIComponent(p.parcel_id)}?from=map`); };
  const [showProDashboard, setShowProDashboard] = useState(false);
  const [showWater, setShowWater] = useState(false);
  const [waterFeatures, setWaterFeatures] = useState(null);
  const [highlightedWater, setHighlightedWater] = useState(null);
  const waterLayerRef = useRef(null);
  const [region, setRegion] = useState(() => getStoredRegion());

  // Mode rattachement admin : verrouiller des parcelles au nom d'un client
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachUser, setAttachUser] = useState(null);
  const [attachRefresh, setAttachRefresh] = useState(0);
  const [attachTick, setAttachTick] = useState(0);
  const attachUserRef = useRef(null);
  const attachOpenRef = useRef(false);
  const attachClickRef = useRef(null);
  // Îlots (sous-parcelles) tracés de la parcelle sélectionnée
  const [parcelIlots, setParcelIlots] = useState([]);
  // Parcelles suivies par l'utilisateur (affichées en violet)
  const [followedIds, setFollowedIds] = useState(() => new Set());
  const followedRef = useRef(followedIds);
  useEffect(() => { followedRef.current = followedIds; }, [followedIds]);
  useEffect(() => { attachUserRef.current = attachUser; }, [attachUser]);
  useEffect(() => { attachOpenRef.current = attachOpen; }, [attachOpen]);

  const initialCenter = region === "mayotte" ? MAYOTTE_CENTER : REUNION_CENTER;
  const initialZoom = region === "mayotte" ? MAYOTTE_ZOOM : REUNION_ZOOM;

  const handleRegionChange = useCallback((newRegion) => {
    setRegion(newRegion);
    setStoredRegion(newRegion);
    setFilterCommune("");
    setFilterCulture("");
    setSelectedParcel(null);
    // Effacer immédiatement les anciennes données pour éviter d'afficher
    // les parcelles/stats de l'autre région pendant le fetch (~1-3s).
    setParcels([]);
    setStats(null);
    setCommunes([]);
    setCultures([]);
    setLoading(true);
    const def = REGION_DEFS[newRegion];
    if (def && mapRef.current) {
      mapRef.current.flyTo(def.center, def.zoom, { duration: 1.0 });
    }
    toast.success(`${def?.flag || ""} Région : ${def?.label || newRegion}`);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const params = { region };
      if (filterCommune) params.commune = filterCommune;
      if (filterCulture) params.culture = filterCulture;
      const [parcelsRes, statsRes] = await Promise.all([getParcels(params), getStats(region)]);
      setParcels(parcelsRes.data);
      setStats(statsRes.data);
      setLastUpdate(new Date());
      console.info(`[MapDashboard] region=${region} loaded: ${parcelsRes.data.length} parcels, total=${statsRes.data?.total_parcels}`);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [filterCommune, filterCulture, region]);

  useEffect(() => {
    const loadInitial = async () => {
      if (!user) {
        try { const res = await authMe(); setUser(res.data); }
        catch { navigate('/', { replace: true }); return; }
      }
      // Check admin/pro status
      try {
        const subRes = await getSubscriptionStatus();
        setIsAdmin(subRes.data.is_admin || false);
        setIsPro(subRes.data.subscription_plan === "veyer_ciel_pro" || subRes.data.is_admin || subRes.data.manual_access || false);
      } catch (err) {
        console.warn('[MapDashboard] subscription-status check failed:', err);
      }
      const [communesRes, culturesRes] = await Promise.all([getCommunes(region), getCultures(region)]);
      setCommunes(communesRes.data);
      setCultures(culturesRes.data);
      // La liste des parcelles est chargée par l'effet [fetchData] (pas de double chargement)
    };
    loadInitial();
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  // Refresh communes/cultures lists when region changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, cu] = await Promise.all([getCommunes(region), getCultures(region)]);
        if (!cancelled) {
          setCommunes(c.data);
          setCultures(cu.data);
        }
      } catch (err) {
        console.warn("[MapDashboard] communes/cultures refresh failed:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [region]);
  // Rafraîchissement léger : la liste complète pèse ~12 Mo, on ne la recharge que toutes les
  // 5 minutes et seulement si l'onglet est visible (bouton « Actualiser » pour forcer).
  useEffect(() => {
    const interval = setInterval(() => { if (!document.hidden) fetchData(); }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Ouvrir directement une parcelle via ?parcel=RE-XXXXX-XXXXXXXX-XXXX
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pid = params.get("parcel");
    if (pid && parcels.length > 0) {
      const found = parcels.find((p) => p.parcel_id === pid);
      if (found) {
        setSelectedParcel(found);
        if (isMobile) setSidebarOpen(false);
      }
    }
  }, [location.search, parcels, isMobile]);

  // Ouvrir le mode rattachement depuis la fiche admin : /map?attach_for=<user_id>
  useEffect(() => {
    if (!isAdmin) return;
    const params = new URLSearchParams(location.search);
    const uid = params.get("attach_for");
    if (!uid) return;
    setAttachOpen(true);
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${API_BASE}/admin/all-users`);
        const data = await res.json();
        const u = (Array.isArray(data) ? data : []).find((x) => x.user_id === uid);
        if (!cancelled && u) setAttachUser(u);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, location.search]);

  // Charge les îlots tracés de la parcelle sélectionnée (affichés dans son contour)
  useEffect(() => {
    let cancelled = false;
    if (!selectedParcel?.parcel_id) { setParcelIlots([]); return; }
    (async () => {
      try {
        const res = await authFetch(`${API_BASE}/parcels/${selectedParcel.parcel_id}/ilots`);
        const data = res.ok ? await res.json() : [];
        if (!cancelled) setParcelIlots((Array.isArray(data) ? data : []).filter(i => i.geometry));
      } catch { if (!cancelled) setParcelIlots([]); }
    })();
    return () => { cancelled = true; };
  }, [selectedParcel?.parcel_id]);

  const geoJsonData = useMemo(() => ({
    type: "FeatureCollection",
    features: parcels.map(p => ({ type: "Feature", properties: p, geometry: p.geometry }))
  }), [parcels]);

  // Charge les parcelles suivies, et se tient à jour quand on suit / retire une parcelle
  useEffect(() => {
    let cancelled = false;
    authFetch(`${API_BASE}/parcels/followed`)
      .then((r) => (r.ok ? r.json() : { parcels: [] }))
      .then((d) => { if (!cancelled) setFollowedIds(new Set((d.parcels || []).map((p) => p.parcel_id))); })
      .catch(() => {});
    const onChange = (e) => {
      const { parcelId, followed } = e.detail || {};
      if (!parcelId) return;
      setFollowedIds((prev) => {
        const next = new Set(prev);
        if (followed) next.add(parcelId); else next.delete(parcelId);
        return next;
      });
    };
    window.addEventListener("veyer:follow-changed", onChange);
    return () => { cancelled = true; window.removeEventListener("veyer:follow-changed", onChange); };
  }, []);

  const parcelStyle = useCallback((feature) => {
    const isSelected = selectedParcel?.parcel_id === feature.properties.parcel_id;
    const isManual = feature.properties.detection_method === "saisie_manuelle";
    const isPending = feature.properties.status === "pending_modification";
    const isLocked = !!feature.properties.locked_by;
    const isFollowed = followedIds.has(feature.properties.parcel_id);
    // Priorité : sélection > suivie > verrouillée > pending > manuelle > défaut (vert)
    let color, fillColor;
    if (isSelected) {
      color = '#E07A5F'; fillColor = '#E07A5F';
    } else if (isFollowed) {
      color = '#6D28D9'; fillColor = '#8B5CF6'; // Violet pour parcelles suivies
    } else if (isLocked) {
      color = '#1D4ED8'; fillColor = '#3B82F6'; // Bleu pour parcelles verrouillées
    } else if (isPending) {
      color = '#E9C46A'; fillColor = '#E9C46A';
    } else if (isManual) {
      color = '#8B5E3C'; fillColor = '#DDA15E';
    } else {
      color = '#2D6A4F'; fillColor = '#7EBC89';
    }
    return {
      color,
      weight: isSelected ? 3 : (isFollowed || isLocked) ? 2.5 : 2,
      fillColor,
      fillOpacity: isSelected ? 0.35 : isFollowed ? 0.35 : isLocked ? 0.30 : 0.15,
      dashArray: isFollowed ? null : isPending ? '5, 5' : isManual ? '3, 3' : null,
    };
  }, [selectedParcel, followedIds]);

  const onEachFeature = useCallback((feature, layer) => {
    const props = feature.properties;
    const method = props.detection_method === 'saisie_manuelle' ? ' [Manuel]' : '';
    const lockBadge = props.locked_by ? '<br/><span style="color:#1D4ED8;font-weight:600">🔒 Parcelle verrouillée</span>' : '';
    const followBadge = followedRef.current.has(props.parcel_id) ? '<br/><span style="color:#6D28D9;font-weight:600">★ Parcelle suivie</span>' : '';
    layer.bindTooltip(
      `<div class="parcel-tooltip"><strong>${props.name}</strong>${method}<br/>${props.commune} - ${props.surface_ha} ha<br/>${props.culture_type}${lockBadge}${followBadge}</div>`,
      { sticky: true, className: 'parcel-tooltip' }
    );
    // Une seule bulle à la fois : le mouseout de Leaflet ne ferme pas toujours le tooltip
    // (survol rapide / parcelles adjacentes) → on ferme explicitement les autres à chaque ouverture.
    layer.on('tooltipopen', () => {
      openTooltipsRef.current.forEach((l) => { if (l !== layer && l.closeTooltip) l.closeTooltip(); });
      openTooltipsRef.current = new Set([layer]);
    });
    layer.on('tooltipclose', () => { openTooltipsRef.current.delete(layer); });
    layer.on('click', () => {
      if (attachOpenRef.current) {
        if (attachUserRef.current) attachClickRef.current?.(props);
        else toast.info("Choisissez d'abord un client dans le panneau.");
        return;
      }
      setSelectedParcel(props);
      if (isMobile) setSidebarOpen(false);
    });
  }, [isMobile]);

  const handleAttachParcel = useCallback(async (props) => {
    const target = attachUserRef.current;
    if (!target) return;
    if (props.locked_by && props.locked_by === target.user_id) {
      toast.info("Déjà rattachée à ce client.");
      return;
    }
    try {
      const res = await authFetch(`${API_BASE}/admin/parcels/${props.parcel_id}/lock`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ target_user_id: target.user_id }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && (data.status === "locked" || data.status === "already_locked")) {
        setParcels((prev) => prev.map((p) => p.parcel_id === props.parcel_id
          ? { ...p, locked_by: target.user_id, locked_by_email: target.email } : p));
        setAttachTick((t) => t + 1);
        setAttachRefresh((r) => r + 1);
        toast.success(`Parcelle rattachée à ${target.name || target.email}`);
      } else {
        const d = data.detail;
        toast.error(typeof d === "string" ? d : (d?.message || "Rattachement impossible"));
      }
    } catch { toast.error("Erreur de connexion"); }
  }, []);
  attachClickRef.current = handleAttachParcel;

  const handleAttachChanged = useCallback((pid, newOwner) => {
    setParcels((prev) => prev.map((p) => p.parcel_id === pid
      ? { ...p, locked_by: newOwner || undefined } : p));
    setAttachTick((t) => t + 1);
  }, []);

  const handleLogout = async () => {
    try { await authLogout(); } catch { }
    navigate('/', { replace: true });
  };

  const handleRefresh = () => { setLoading(true); fetchData(); toast.success("Donnees actualisees"); };

  const handleRunDetection = async () => {
    setDetecting(true);
    toast.info("Detection en cours... Analyse des images satellite");
    try {
      const res = await runDetection();
      setDetectionResult(res.data);
      toast.success(`${res.data.parcels_detected} parcelles detectees (${res.data.total_surface_ha} ha)`);
      const [communesRes, culturesRes] = await Promise.all([getCommunes(region), getCultures(region)]);
      setCommunes(communesRes.data);
      setCultures(culturesRes.data);
      setFilterCommune(""); setFilterCulture("");
      fetchData();
    } catch { toast.error("Erreur de detection"); }
    finally { setDetecting(false); }
  };

  const cycleLayer = () => {
    const layers = Object.keys(TILE_LAYERS);
    setActiveLayer(layers[(layers.indexOf(activeLayer) + 1) % layers.length]);
  };

  const toggleWater = () => {
    if (!waterFeatures) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/agro/water-features`).then(r=>r.json()).then(setWaterFeatures).catch(()=>{});
    }
    setShowWater(!showWater);
    setHighlightedWater(null);
  };

  const waterStyle = useCallback((feature) => {
    const isHighlighted = highlightedWater && feature.properties?.name === highlightedWater;
    return {
      color: isHighlighted ? '#EF4444' : '#2196F3',
      weight: isHighlighted ? 4 : 2,
      opacity: isHighlighted ? 1 : 0.7,
    };
  }, [highlightedWater]);

  const onEachWaterFeature = useCallback((feature, layer) => {
    const name = feature.properties?.name || 'Cours d\'eau';
    layer.bindTooltip(name, { sticky: true, className: 'text-xs' });
    layer.on('tooltipopen', () => {
      openTooltipsRef.current.forEach((l) => { if (l !== layer && l.closeTooltip) l.closeTooltip(); });
      openTooltipsRef.current = new Set([layer]);
    });
    layer.on('tooltipclose', () => { openTooltipsRef.current.delete(layer); });
    layer.on('click', () => {
      setHighlightedWater(prev => prev === name ? null : name);
    });
  }, []);

  // Cleanup GPS watcher on unmount
  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, []);

  const handleGeolocate = () => {
    if (!navigator.geolocation) {
      toast.error("Géolocalisation non disponible sur ce navigateur");
      return;
    }

    // If location is already being tracked, just recenter the map on it
    if (userLocation && watchIdRef.current !== null) {
      if (mapRef.current) mapRef.current.flyTo([userLocation.lat, userLocation.lng], 17, { duration: 0.8 });
      return;
    }

    setLocating(true);
    toast.info("Recherche de votre position GPS...");

    let firstFix = true;
    const startWatch = (highAccuracy) => {
      watchIdRef.current = navigator.geolocation.watchPosition(
        (pos) => {
          const { latitude, longitude, accuracy } = pos.coords;
          setUserLocation({ lat: latitude, lng: longitude, accuracy });
          if (firstFix) {
            firstFix = false;
            setLocating(false);
            if (mapRef.current) mapRef.current.flyTo([latitude, longitude], 17, { duration: 1 });
            toast.success(`Position trouvée (±${Math.round(accuracy)}m)`);
          }
        },
        (err) => {
          if (highAccuracy && err.code !== 1) {
            // Retry without high accuracy
            if (watchIdRef.current !== null) {
              navigator.geolocation.clearWatch(watchIdRef.current);
              watchIdRef.current = null;
            }
            startWatch(false);
            return;
          }
          setLocating(false);
          if (watchIdRef.current !== null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
          }
          if (err.code === 1) {
            toast.error("Accès GPS refusé. Allez dans Réglages > Safari > Localisation et autorisez l'accès, puis rafraîchissez la page.");
          } else if (err.code === 2) {
            toast.error("Position GPS indisponible. Vérifiez que le GPS est activé dans vos réglages (Réglages > Confidentialité > Service de localisation).");
          } else {
            toast.error("Délai d'attente GPS dépassé. Réessayez en extérieur avec une bonne réception.");
          }
        },
        { enableHighAccuracy: highAccuracy, timeout: highAccuracy ? 15000 : 25000, maximumAge: 5000 }
      );
    };
    startWatch(true);
  };

  // Recherche d'adresse → centrer la carte sur le résultat sélectionné
  const handleAddressSelect = ({ lat, lon, label }) => {
    setMapPosition({ lat, lng: lon, zoom: 17 });
    if (mapRef.current) mapRef.current.flyTo([lat, lon], 17, { duration: 1.2 });
    if (isMobile) setSidebarOpen(false);
    toast.success(`📍 ${label}`);
  };

  const handleCadastreResults = (results) => {
    setCadastreShapes(results || []);
    if (!results?.length || !mapRef.current) return;
    try {
      const bounds = L.geoJSON({ type: "FeatureCollection", features: results.map((r) => ({ type: "Feature", geometry: r.geometry })) }).getBounds();
      if (bounds.isValid()) mapRef.current.flyToBounds(bounds, { padding: [60, 60], maxZoom: 17, duration: 1 });
    } catch { /* contour illisible : la carte ne bouge pas */ }
  };

  // « Créer ma parcelle à partir du cadastre » : le tracé manuel s'ouvre avec le contour officiel
  const handleCreateFromCadastre = (r) => {
    const pts = cadastreToPoints(r.geometry);
    if (!pts.length) return;
    const c = pts.reduce((a, p) => ({ lat: a.lat + p.lat / pts.length, lon: a.lon + p.lon / pts.length }), { lat: 0, lon: 0 });
    setManualPrefill({ points: pts, commune: r.commune, name: `Parcelle ${cadLabel(r)}`, center: c });
    setSidebarOpen(false);
    setViewMode("manual");
  };

  // « Convertir en parcelle saisie » : la nouvelle parcelle apparaît sur la carte et sa fiche s'ouvre
  const handleParcelConverted = (created) => {
    fetchData();
    if (created) { setSelectedParcel(created); if (isMobile) setSidebarOpen(false); }
  };

  const cadastreLayer = cadastreShapes.length > 0 && (
    <GeoJSON key={`cad-${cadastreShapes.map((r) => r.idu).join("-")}`} interactive={false}
      data={{ type: "FeatureCollection", features: cadastreShapes.map((r) => ({ type: "Feature", properties: {}, geometry: r.geometry })) }}
      style={{ color: "#E07A5F", weight: 3, dashArray: "8 5", fillColor: "#E07A5F", fillOpacity: 0.1 }} />
  );

  // Sub-views
  if (viewMode === "manual") {
    return (
      <div className="h-screen w-screen" data-testid="map-dashboard">
        <ManualParcelEditor
          onBack={() => { setViewMode("map"); setManualPrefill(null); }}
          onParcelCreated={() => { setViewMode("map"); setManualPrefill(null); setCadastreShapes([]); fetchData(); }}
          prefill={manualPrefill}
          initialCenter={[mapPosition.lat, mapPosition.lng]}
          initialZoom={mapPosition.zoom}
        />
      </div>
    );
  }
  if (viewMode === "admin") {
    return (
      <div className="h-screen w-screen" data-testid="map-dashboard">
        <AdminUsers onBack={() => setViewMode("map")} />
      </div>
    );
  }
  if (phytoParcel) {
    return (
      <div className="h-screen w-screen" data-testid="map-dashboard">
        <CarnetParcelle parcel={phytoParcel} onBack={() => setPhytoParcel(null)} />
      </div>
    );
  }
  if (showProDashboard) {
    return (
      <div className="h-screen w-screen" data-testid="map-dashboard">
        <ProDashboard user={user} onBack={() => setShowProDashboard(false)} onPhyto={openCarnet} isPro={isPro} />
      </div>
    );
  }

  // ==================== MOBILE LAYOUT ====================
  if (isMobile) {
    return (
      <div className="h-screen w-screen flex flex-col relative" data-testid="map-dashboard">
        {/* Mobile top bar */}
        <div className="absolute top-0 left-0 right-0 z-[1000] px-3 pt-3 pb-1 pointer-events-none">
          <div className="flex items-center justify-between pointer-events-auto">
            <div className="flex items-center gap-1.5">
              <Button variant="outline" size="icon" className="floating-panel h-9 w-9 border-0" onClick={() => navigate('/dashboard')} data-testid="mobile-back-dashboard-btn" title="Tableau de bord">
                <Layers className="w-4 h-4" style={{ color: '#2D6A4F' }} />
              </Button>
              <Button variant="outline" size="icon" className="floating-panel h-9 w-9 border-0" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="toggle-sidebar-btn">
                {sidebarOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
              </Button>
              <Button variant="outline" size="icon" className="floating-panel h-9 w-9 border-0" onClick={handleGeolocate} disabled={locating} data-testid="geolocate-btn" title="Ma position GPS">
                <Navigation className={`w-4 h-4 ${locating ? 'animate-pulse' : ''}`} style={{ color: '#2D6A4F' }} />
              </Button>
            </div>
            <div className="floating-panel px-2.5 py-1.5 flex items-center gap-1.5">
              <img src="/veyer-ciel-logo.png" alt="Véyèr CIEL" className="h-5" />
            </div>
            <div className="flex items-center gap-1.5">
              <RegionToggle region={region} onChange={handleRegionChange} compact />
              <Button variant="outline" size="icon" className="floating-panel h-9 w-9 border-0" onClick={cycleLayer} data-testid="toggle-layer-btn">
                <Layers className="w-4 h-4" />
              </Button>
              {user?.picture && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-white/50 shadow-sm cursor-pointer" data-testid="user-avatar-menu" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-xl">
                    <DropdownMenuItem className="text-xs cursor-pointer gap-2" onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                      <Layers className="w-3.5 h-3.5" /> Tableau de bord
                    </DropdownMenuItem>
                    <DropdownMenuItem className="text-xs cursor-pointer gap-2" onClick={() => navigate("/account")} data-testid="menu-account">
                      <User className="w-3.5 h-3.5" /> Mon Compte
                    </DropdownMenuItem>
                    {!user?.is_coordinator && !user?.is_admin && (
                      <DropdownMenuItem className="text-xs cursor-pointer gap-2" onClick={() => navigate("/account#convention")} data-testid="menu-my-convention">
                        <FileSignature className="w-3.5 h-3.5" /> Ma convention
                      </DropdownMenuItem>
                    )}
                    {user?.is_coordinator && !user?.is_admin && (
                      <DropdownMenuItem className="text-xs cursor-pointer gap-2" onClick={() => navigate("/conventions")} data-testid="menu-conventions">
                        <FileSignature className="w-3.5 h-3.5" /> Conventions
                      </DropdownMenuItem>
                    )}
                    {user?.is_admin && (
                      <DropdownMenuItem className="text-xs cursor-pointer gap-2" onClick={() => navigate("/admin-dashboard")} data-testid="menu-admin">
                        <Shield className="w-3.5 h-3.5" /> Admin <PendingBadge count={pendingLocks} className="ml-auto" />
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-xs cursor-pointer gap-2 text-red-600" onClick={handleLogout} data-testid="menu-logout">
                      <LogOut className="w-3.5 h-3.5" /> Deconnexion
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </div>

        {/* Map */}
        <div className="flex-1 relative">
          <MapContainer center={initialCenter} zoom={initialZoom} className="h-full w-full" zoomControl={false} maxZoom={MAX_ZOOM} ref={mapRef}>
            <MapPositionTracker onMove={setMapPosition} />
            <TooltipDragCleaner />
            <TileLayer key={activeLayer} url={TILE_LAYERS[activeLayer].url} attribution={TILE_LAYERS[activeLayer].attribution} maxZoom={MAX_ZOOM} maxNativeZoom={TILE_LAYERS[activeLayer].maxNativeZoom} />
            <GeoJSON key={`${selectedParcel?.parcel_id || 'n'}-${region}-${parcels.length}-${filterCommune}-${filterCulture}-${attachTick}-${followedIds.size}`} data={geoJsonData} style={parcelStyle} onEachFeature={onEachFeature} />
          {parcelIlots.length > 0 && (
            <GeoJSON
              key={`ilots-${selectedParcel?.parcel_id}-${parcelIlots.length}`}
              data={{ type: "FeatureCollection", features: parcelIlots.map(i => ({ type: "Feature", properties: i, geometry: i.geometry })) }}
              style={{ color: "#2f6a92", weight: 2, dashArray: "5 4", fillColor: "#2f6a92", fillOpacity: 0.12 }}
              onEachFeature={(f, layer) => layer.bindTooltip(`<div class="parcel-tooltip"><strong>${f.properties.name}</strong>${f.properties.culture ? "<br/>" + f.properties.culture : ""}</div>`, { sticky: true, className: 'parcel-tooltip' })}
            />
          )}
            {showWater && waterFeatures && <GeoJSON key={`water-${highlightedWater || 'none'}`} data={waterFeatures} style={waterStyle} onEachFeature={onEachWaterFeature} />}
            {cadastreLayer}
            <UserLocationLayer location={userLocation} />
            {selectedParcel && <FlyToParcel parcel={selectedParcel} mobileSheet />}
          </MapContainer>

          {/* Mobile weather widget */}
          <div className="absolute bottom-7 right-2 z-[998]">
            <WeatherWidget lat={mapPosition.lat} lon={mapPosition.lng} />
          </div>

          {/* Mobile sidebar overlay */}
          {sidebarOpen && (
            <div className="absolute inset-0 z-[999] bg-black/30" onClick={() => setSidebarOpen(false)}>
              <div className="absolute top-0 left-0 bottom-0 w-[85%] max-w-[340px] bg-white shadow-xl mobile-scroll-container" onClick={(e) => e.stopPropagation()} style={{ overscrollBehavior: 'contain' }}>
                <Sidebar stats={stats} communes={communes} cultures={cultures} filterCommune={filterCommune} filterCulture={filterCulture} setFilterCommune={setFilterCommune} setFilterCulture={setFilterCulture} parcels={parcels} onSelectParcel={(p) => { setSelectedParcel(p); setSidebarOpen(false); }} selectedParcelId={selectedParcel?.parcel_id} detecting={detecting} detectionResult={detectionResult} onRunDetection={handleRunDetection} activeLayer={activeLayer} isAdmin={isAdmin} onAddressSelect={handleAddressSelect} onCadastreResults={handleCadastreResults} onCreateFromCadastre={handleCreateFromCadastre} onParcelConverted={handleParcelConverted} />
              </div>
            </div>
          )}

          {/* Mobile parcel detail overlay */}
          {selectedParcel && (
            <div className="absolute inset-x-0 bottom-0 z-[999] max-h-[70vh] bg-white rounded-t-2xl shadow-xl border-t border-black/5 overflow-hidden" style={{ overscrollBehavior: 'contain', touchAction: 'pan-y' }}>
              <div className="w-10 h-1 bg-black/10 rounded-full mx-auto mt-2 mb-1" />
              <div className="overflow-y-auto mobile-scroll-container" style={{ maxHeight: 'calc(70vh - 12px)' }}>
                <ParcelDetail parcel={selectedParcel} onClose={() => setSelectedParcel(null)} onRefresh={fetchData} user={user} onPhyto={openCarnet} isPro={isPro} />
              </div>
            </div>
          )}
        </div>

        {/* Mobile bottom navigation */}
        <div className="shrink-0 bg-white border-t border-black/5 px-2 py-1.5 pb-safe z-[1001]" style={{ paddingBottom: 'max(6px, env(safe-area-inset-bottom))' }}>
          <div className="flex items-center justify-around">
            {/* Detection group — admin only */}
            {isAdmin && (
              <button onClick={handleRunDetection} disabled={detecting} className="flex flex-col items-center gap-0.5 py-1 px-3" data-testid="mobile-detection-btn">
                <Radar className={`w-5 h-5 ${detecting ? 'animate-pulse' : ''}`} style={{ color: detecting ? '#E07A5F' : '#2D6A4F' }} />
                <span className="text-[9px] font-semibold" style={{ color: detecting ? '#E07A5F' : '#2D6A4F' }}>{detecting ? 'Détect...' : 'Détection'}</span>
              </button>
            )}
            <button onClick={handleRefresh} className="flex flex-col items-center gap-0.5 py-1 px-3" data-testid="mobile-refresh-btn">
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} style={{ color: '#4A4A4A' }} />
              <span className="text-[9px] font-medium" style={{ color: '#4A4A4A' }}>Actualiser</span>
            </button>

            <div className="w-px h-8 bg-black/10" />

            {/* Tools group */}
            {isPro && (
              <button onClick={() => setViewMode("manual")} className="flex flex-col items-center gap-0.5 py-1 px-3" data-testid="mobile-manual-btn">
                <PenLine className="w-5 h-5" style={{ color: '#8B5E3C' }} />
                <span className="text-[9px] font-medium" style={{ color: '#8B5E3C' }}>Saisie</span>
              </button>
            )}
            <button onClick={cycleLayer} className="flex flex-col items-center gap-0.5 py-1 px-3" data-testid="mobile-layer-btn">
              <Layers className="w-5 h-5" style={{ color: '#4A4A4A' }} />
              <span className="text-[9px] font-medium" style={{ color: '#4A4A4A' }}>{TILE_LAYERS[activeLayer].name}</span>
            </button>
            <button onClick={toggleWater} className="flex flex-col items-center gap-0.5 py-1 px-3" data-testid="mobile-water-btn">
              <Droplets className="w-5 h-5" style={{ color: showWater ? '#2196F3' : '#4A4A4A' }} />
              <span className="text-[9px] font-medium" style={{ color: showWater ? '#2196F3' : '#4A4A4A' }}>Eau</span>
            </button>
            {isPro && (
              <button onClick={() => setShowProDashboard(true)} className="flex flex-col items-center gap-0.5 py-1 px-3" data-testid="mobile-pro-dashboard-btn">
                <Star className="w-5 h-5" style={{ color: '#2D6A4F' }} />
                <span className="text-[9px] font-medium" style={{ color: '#2D6A4F' }}>Suivi</span>
              </button>
            )}
            {isAdmin && (
              <button onClick={() => navigate('/admin-dashboard')} className="flex flex-col items-center gap-0.5 py-1 px-3" data-testid="mobile-admin-btn">
                <span className="relative">
                  <LayoutDashboard className="w-5 h-5" style={{ color: '#8B5CF6' }} />
                  <PendingBadge count={pendingLocks} className="absolute -top-1.5 -right-2.5" />
                </span>
                <span className="text-[9px] font-medium" style={{ color: '#8B5CF6' }}>Admin</span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ==================== DESKTOP LAYOUT ====================
  return (
    <div className="h-screen w-screen relative overflow-hidden" data-testid="map-dashboard">
      <MapContainer center={initialCenter} zoom={initialZoom} className="h-full w-full" zoomControl={false} maxZoom={MAX_ZOOM} ref={mapRef} data-testid="leaflet-map">
        <MapPositionTracker onMove={setMapPosition} />
        <TooltipDragCleaner />
        <TileLayer key={activeLayer} url={TILE_LAYERS[activeLayer].url} attribution={TILE_LAYERS[activeLayer].attribution} maxZoom={MAX_ZOOM} maxNativeZoom={TILE_LAYERS[activeLayer].maxNativeZoom} />
        <GeoJSON key={`${selectedParcel?.parcel_id || 'n'}-${region}-${parcels.length}-${filterCommune}-${filterCulture}`} data={geoJsonData} style={parcelStyle} onEachFeature={onEachFeature} />
        {showWater && waterFeatures && <GeoJSON key={`water-desktop-${highlightedWater || 'none'}`} data={waterFeatures} style={waterStyle} onEachFeature={onEachWaterFeature} />}
        {cadastreLayer}
        <UserLocationLayer location={userLocation} />
        {selectedParcel && <FlyToParcel parcel={selectedParcel} />}
      </MapContainer>

      {/* Desktop Top Bar */}
      <div className="absolute top-4 left-4 right-4 z-[1000] flex items-center justify-between pointer-events-none">
        <div className="flex items-center gap-2 pointer-events-auto">
          <Button data-testid="back-dashboard-btn" variant="outline" className="floating-panel h-10 border-0 px-3 text-xs font-semibold gap-1.5" style={{ color: '#2D6A4F' }} onClick={() => navigate('/dashboard')}>
            <ChevronDown className="w-3.5 h-3.5 rotate-90" />
            <span>Tableau de bord</span>
          </Button>
          <Button data-testid="toggle-sidebar-btn" variant="outline" size="icon" className="floating-panel h-10 w-10 border-0" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </Button>
          <Button data-testid="geolocate-btn" variant="outline" size="icon" className="floating-panel h-10 w-10 border-0" onClick={handleGeolocate} disabled={locating} title="Ma position GPS">
            <Navigation className={`w-4 h-4 ${locating ? 'animate-pulse' : ''}`} style={{ color: '#2D6A4F' }} />
          </Button>
        </div>

        <div className="flex items-center gap-2 pointer-events-auto">
          {/* Logo */}
          <div className="floating-panel px-3 py-2 flex items-center gap-2">
            <img src="/veyer-ciel-logo.png" alt="Véyèr CIEL" className="h-6" />
            <div className="w-px h-4 bg-black/10 mx-0.5" />
            <div className="w-2 h-2 rounded-full pulse-dot" style={{ background: '#2D6A4F' }} />
            <span className="text-[10px] font-medium" style={{ color: '#4A4A4A' }}>
              {lastUpdate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>

          <Button data-testid="refresh-btn" variant="outline" size="icon" className="floating-panel h-10 w-10 border-0" onClick={handleRefresh}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <RegionToggle region={region} onChange={handleRegionChange} />
          <Button data-testid="toggle-layer-btn" variant="outline" className="floating-panel h-10 border-0 px-3 text-xs font-medium gap-1.5" onClick={cycleLayer}>
            <Layers className="w-3.5 h-3.5" /><span>{TILE_LAYERS[activeLayer].name}</span>
          </Button>

          {/* Outils Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="outils-dropdown-btn" variant="outline" className="floating-panel h-10 border-0 px-3 text-xs font-semibold gap-1.5" style={{ color: '#2D6A4F' }}>
                <Wrench className="w-3.5 h-3.5" />
                <span>Outils</span>
                <ChevronDown className="w-3 h-3 ml-0.5 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52 z-[9999]" sideOffset={8}>
              {isAdmin && (
                <>
                  <DropdownMenuItem data-testid="outils-detection-btn" className="gap-2 cursor-pointer" style={{ color: detecting ? '#E07A5F' : '#2D6A4F' }} onClick={handleRunDetection} disabled={detecting}>
                    {detecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radar className="w-4 h-4" />}
                    <span className="font-medium">{detecting ? "Détection..." : "Détection ML (admin)"}</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem data-testid="outils-water-btn" className="gap-2 cursor-pointer" style={{ color: showWater ? '#2196F3' : '#4A4A4A' }} onClick={toggleWater}>
                <Droplets className="w-4 h-4" />
                <span className="font-medium">Cours d'eau {showWater ? '(actif)' : ''}</span>
              </DropdownMenuItem>
              {isPro && (
                <>
                  <DropdownMenuItem data-testid="outils-manual-btn" className="gap-2 cursor-pointer" style={{ color: '#8B5E3C' }} onClick={() => setViewMode("manual")}>
                    <PenLine className="w-4 h-4" />
                    <span className="font-medium">Saisie manuelle</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="outils-pro-dashboard-btn" className="gap-2 cursor-pointer" style={{ color: '#2D6A4F' }} onClick={() => setShowProDashboard(true)}>
                    <Star className="w-4 h-4" />
                    <span className="font-medium">Mes parcelles</span>
                  </DropdownMenuItem>
                </>
              )}
              {user?.is_coordinator && !isAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="outils-conventions-btn" className="gap-2 cursor-pointer" style={{ color: '#0E7490' }} onClick={() => navigate('/conventions')}>
                    <FileSignature className="w-4 h-4" />
                    <span className="font-medium">Conventions</span>
                  </DropdownMenuItem>
                </>
              )}
              {isAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="outils-admin-btn" className="gap-2 cursor-pointer" style={{ color: '#8B5CF6' }} onClick={() => navigate('/admin-dashboard')}>
                    <LayoutDashboard className="w-4 h-4" />
                    <span className="font-medium">Tableau de bord admin</span>
                    <PendingBadge count={pendingLocks} className="ml-auto" />
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="outils-users-btn" className="gap-2 cursor-pointer" style={{ color: '#4A4A4A' }} onClick={() => setViewMode("admin")}>
                    <Users className="w-4 h-4" />
                    <span className="font-medium">Comptes</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="outils-attach-btn" className="gap-2 cursor-pointer" style={{ color: '#1f5c39' }} onClick={() => setAttachOpen(true)}>
                    <Link2 className="w-4 h-4" />
                    <span className="font-medium">Rattacher des parcelles</span>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {user && (
            <div className="floating-panel flex items-center gap-2 px-3 py-1.5">
              {user.picture && <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />}
              <span className="text-xs font-medium" style={{ color: '#1A1A1A' }}>{user.name}</span>
              <Button data-testid="logout-btn" variant="ghost" size="icon" className="h-7 w-7" onClick={handleLogout}>
                <LogOut className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Desktop Sidebar */}
      {sidebarOpen && (
        <div className="absolute top-20 left-4 bottom-4 z-[999] w-80 slide-in-left">
          <Sidebar stats={stats} communes={communes} cultures={cultures} filterCommune={filterCommune} filterCulture={filterCulture} setFilterCommune={setFilterCommune} setFilterCulture={setFilterCulture} parcels={parcels} onSelectParcel={setSelectedParcel} selectedParcelId={selectedParcel?.parcel_id} detecting={detecting} detectionResult={detectionResult} onRunDetection={handleRunDetection} activeLayer={activeLayer} isAdmin={isAdmin} onAddressSelect={handleAddressSelect} onCadastreResults={handleCadastreResults} onCreateFromCadastre={handleCreateFromCadastre} onParcelConverted={handleParcelConverted} />
        </div>
      )}

      {/* Desktop Detail Panel */}
      {selectedParcel && (
        <div className="absolute top-20 right-4 bottom-4 z-[1001] w-96 slide-in-right">
          <ParcelDetail parcel={selectedParcel} onClose={() => setSelectedParcel(null)} onRefresh={fetchData} user={user} onPhyto={openCarnet} isPro={isPro} />
        </div>
      )}

      {/* Bandeau + panneau de rattachement (admin) */}
      {attachOpen && attachUser && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1002]">
          <div className="floating-panel px-4 py-2 rounded-full flex items-center gap-2 text-xs font-semibold shadow-lg text-white" style={{ background: '#1f5c39' }}>
            <span className="w-2 h-2 rounded-full bg-emerald-300 animate-pulse" />
            Rattachement pour <span className="px-2 py-0.5 rounded-full bg-white/20">{attachUser.name || attachUser.email}</span>
          </div>
        </div>
      )}
      {attachOpen && (
        <div className="absolute top-20 right-4 bottom-4 z-[1002] w-96 slide-in-right">
          <AdminAttachPanel
            targetUser={attachUser}
            onSelectUser={setAttachUser}
            onExit={() => { setAttachOpen(false); setAttachUser(null); if (location.search.includes('attach_for')) navigate('/map', { replace: true }); }}
            refreshSignal={attachRefresh}
            onChanged={handleAttachChanged}
          />
        </div>
      )}

      {/* Desktop Weather Widget */}
      {!selectedParcel && (
        <div className="absolute bottom-4 right-4 z-[998]">
          <WeatherWidget lat={mapPosition.lat} lon={mapPosition.lng} />
        </div>
      )}
    </div>
  );
}
